import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "cs_attribution";
const SESSION_KEY = "cs_funnel_session";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/recruit`;

const KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "source_channel", "source_campaign", "creative_id", "partner_ref",
  "ambassador_ref", "opportunity_id",
];

function readAttribution() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {};
  KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) fromUrl[key] = value.slice(0, 160);
  });
  // Aliases used by short social links and QR codes.
  if (!fromUrl.utm_source && params.get("src")) fromUrl.utm_source = params.get("src").slice(0, 120);
  if (!fromUrl.ambassador_ref && params.get("amb")) fromUrl.ambassador_ref = params.get("amb").slice(0, 120);
  if (!fromUrl.partner_ref && params.get("ref")) fromUrl.partner_ref = params.get("ref").slice(0, 120);

  let stored = {};
  try {
    stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    stored = {};
  }

  // First touch wins; a later visit never erases the campaign that actually brought the person.
  const merged = { ...fromUrl, ...stored };
  merged.referrer_url = stored.referrer_url || document.referrer || null;
  merged.landing_page = stored.landing_page || window.location.pathname;
  if (!merged.source_channel && merged.utm_source) merged.source_channel = merged.utm_source;
  if (!merged.source_campaign && merged.utm_campaign) merged.source_campaign = merged.utm_campaign;

  const clean = {};
  Object.entries(merged).forEach(([k, v]) => {
    if (v) clean[k] = v;
  });
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
  } catch {
    /* private browsing: attribution still travels with this submission */
  }
  return clean;
}

function readSession() {
  let id = window.sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/** Attribution + anonymous funnel telemetry for the recruitment landing page. */
export function useFunnel(lang) {
  const [attribution, setAttribution] = useState({});
  const [sessionId, setSessionId] = useState("");
  const sent = useRef(new Set());

  useEffect(() => {
    setAttribution(readAttribution());
    setSessionId(readSession());
  }, []);

  const track = (event, extra = {}) => {
    if (!sessionId) return;
    const key = `${event}:${extra.step ?? ""}`;
    if (sent.current.has(key)) return;
    // A remount (or StrictMode's double mount) must not double-count a funnel step.
    const persistedKey = `cs_funnel_sent:${sessionId}:${key}`;
    if (window.sessionStorage.getItem(persistedKey)) return;
    sent.current.add(key);
    window.sessionStorage.setItem(persistedKey, "1");
    fetch(`${API}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, session_id: sessionId, language: lang, attribution, ...extra }),
    }).catch(() => {});
  };

  return { attribution, sessionId, track };
}
